import React from 'react';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const TrainingTypeAnalytics = ({ data }) => {
  const mandatoryData = data?.mandatory?.categories?.map(cat => ({
    name: cat?.name,
    rate: cat?.rate
  })) || [];

  const skillBasedData = data?.skillBased?.categories?.map(cat => ({
    name: cat?.name,
    rate: cat?.rate
  })) || [];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground">{payload?.[0]?.payload?.name}</p>
          <p className="text-sm text-muted-foreground">
            Completion: <span className="font-semibold text-primary">{payload?.[0]?.value}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="mb-6">
      <h2 className="text-2xl font-semibold text-foreground mb-4">Training Type Analytics</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Mandatory Training */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Icon name="Shield" size={20} className="text-blue-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Mandatory Training</h3>
                <p className="text-sm text-muted-foreground">
                  {data?.mandatory?.completed} of {data?.mandatory?.total} completed
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-foreground">{data?.mandatory?.completionRate}%</p>
              <p className="text-xs text-muted-foreground">Overall Rate</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-muted/50 rounded-lg">
            <div className="text-center">
              <p className="text-2xl font-bold text-success">{data?.mandatory?.completed}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </div>
            <div className="text-center border-l border-r border-border">
              <p className="text-2xl font-bold text-warning">{data?.mandatory?.inProgress}</p>
              <p className="text-xs text-muted-foreground">In Progress</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-destructive">{data?.mandatory?.overdue}</p>
              <p className="text-xs text-muted-foreground">Overdue</p>
            </div>
          </div>

          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={mandatoryData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }}
                  domain={[0, 100]}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="rate" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Skill-Based Training */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Icon name="Award" size={20} className="text-purple-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Skill-Based Training</h3>
                <p className="text-sm text-muted-foreground">
                  {data?.skillBased?.completed} of {data?.skillBased?.total} completed
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-foreground">{data?.skillBased?.completionRate}%</p>
              <p className="text-xs text-muted-foreground">Overall Rate</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-muted/50 rounded-lg">
            <div className="text-center">
              <p className="text-2xl font-bold text-success">{data?.skillBased?.completed}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </div>
            <div className="text-center border-l border-r border-border">
              <p className="text-2xl font-bold text-warning">{data?.skillBased?.inProgress}</p>
              <p className="text-xs text-muted-foreground">In Progress</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-muted-foreground">{data?.skillBased?.notStarted}</p>
              <p className="text-xs text-muted-foreground">Not Started</p>
            </div>
          </div>

          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={skillBasedData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }}
                  domain={[0, 100]}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="rate" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingTypeAnalytics;